import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Badge, UserBadge, UserLevel, LevelType } from '@/types/gamification';
import { LEVEL_CONFIG, LEVEL_ORDER } from '@/types/gamification';
import { ensureSession, fetchUserLevelRow, fetchPublishedCount, combinePoints } from '@/lib/engagementPoints';

export function useGamification() {
  const { user, isHydrated } = useAuth();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isHydrated) return;
    if (!user) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    const fetchGamificationData = async () => {
      setIsLoading(true);
      await ensureSession();

      const [badgesRes, userBadgesRes, levelData, published] = await Promise.all([
        supabase.from('badges').select('*').order('points', { ascending: true }),
        supabase.from('user_badges').select('*, badge:badges(*)').eq('user_id', user.id),
        fetchUserLevelRow(user.id),
        fetchPublishedCount(user.id),
      ]);
      if (cancelled) return;

      if (badgesRes.data) setBadges(badgesRes.data as Badge[]);
      if (userBadgesRes.data) setUserBadges(userBadgesRes.data as UserBadge[]);

      const total = combinePoints((levelData as { total_points?: number } | null)?.total_points || 0, published);
      let level: LevelType = 'heart_opener';
      for (const l of LEVEL_ORDER) if (total >= LEVEL_CONFIG[l].minPoints) level = l;
      setUserLevel({
        ...((levelData as unknown as UserLevel) ?? { id: '', user_id: user.id, badges_earned: 0, updated_at: '' }),
        total_points: total,
        current_level: level,
      } as UserLevel);

      setIsLoading(false);
    };

    fetchGamificationData();
    return () => { cancelled = true; };
  }, [isHydrated, user?.id]);

  const earnedBadgeIds = userBadges.map(ub => ub.badge_id);
  const earnedBadges = badges.filter(b => earnedBadgeIds.includes(b.id));
  const unearnedBadges = badges.filter(b => !earnedBadgeIds.includes(b.id));

  const currentLevelIndex = userLevel 
    ? LEVEL_ORDER.indexOf(userLevel.current_level)
    : 0;
  
  const nextLevel = currentLevelIndex < LEVEL_ORDER.length - 1 
    ? LEVEL_ORDER[currentLevelIndex + 1]
    : null;
  
  const currentLevelConfig = userLevel 
    ? LEVEL_CONFIG[userLevel.current_level]
    : LEVEL_CONFIG.heart_opener;
  
  const nextLevelConfig = nextLevel ? LEVEL_CONFIG[nextLevel] : null;
  
  const pointsToNextLevel = nextLevelConfig 
    ? nextLevelConfig.minPoints - (userLevel?.total_points || 0)
    : 0;

  const progressToNextLevel = nextLevelConfig && userLevel
    ? Math.min(100, ((userLevel.total_points - currentLevelConfig.minPoints) / (nextLevelConfig.minPoints - currentLevelConfig.minPoints)) * 100)
    : 100;

  return {
    badges,
    userBadges,
    earnedBadges,
    unearnedBadges,
    userLevel,
    currentLevelConfig,
    nextLevelConfig,
    pointsToNextLevel,
    progressToNextLevel,
    isLoading,
  };
}
